/**
 * Yjs CRDT Setup
 *
 * Creates and manages the distributed state for a room.
 */

import * as Y from 'yjs'
import { IndexeddbPersistence } from 'y-indexeddb'
import type {
  Participant,
  RoomMeta,
  QuickActionKey,
  Role,
  MicMode,
  ChatMode,
  POIMode,
  HierarchyRoomType,
} from '@/lib/types'

// ============= Room Document Structure =============

export interface RoomDocument {
  doc: Y.Doc
  participants: Y.Map<Participant>
  speakingQueue: Y.Array<string>
  pois: Y.Map<POIEntry>
  messages: Y.Array<MessageEntry>
  meta: Y.Map<unknown>
  roleAssignments: Y.Map<RoleAssignmentEntry>
  // ICS Hierarchy extensions
  hierarchyMeta: Y.Map<unknown>
  escalations: Y.Array<EscalatedReportEntry>
  broadcasts: Y.Array<CommandBroadcastEntry>
  sectors: Y.Map<SectorInfoEntry>
  // Command center room management extensions
  organizerAssignments: Y.Map<OrganizerAssignmentEntry>
  remoteCommands: Y.Array<RemoteCommandEntry>
  persistence: IndexeddbPersistence | null
}

// ============= ICS Hierarchy Entry Types =============

export interface EscalatedReportEntry {
  id: string
  sourceRoomId: string
  sourceRoomName: string
  originalMessageId: string
  originalMessageContent?: string
  originalMessageType: 'text' | 'voice' | 'poi'
  escalatedBy: string
  escalatedByName: string
  priority: 'routine' | 'urgent' | 'critical'
  timestamp: number
  signature: string
  acknowledged: boolean
  acknowledgedBy?: string
  acknowledgedAt?: number
}

export interface CommandBroadcastEntry {
  id: string
  commandRoomId: string
  targetRoomIds: string[] | 'all'
  messageId: string
  messageContent: string
  messageType: 'text' | 'voice'
  broadcastBy: string
  broadcastByName: string
  priority: 'routine' | 'urgent' | 'critical'
  timestamp: number
  signature: string
}

export interface SectorInfoEntry {
  roomId: string
  name: string
  participantCount: number
  locationLat?: number
  locationLng?: number
  leadPeerId?: string
  leadName?: string
  status: 'active' | 'offline' | 'alert' | 'moving'
  lastActivity: number
  pendingEscalations: number
  // Boundary data for sector geofence
  centerLat: number
  centerLng: number
  radiusMeters: number
  // Encrypted room secret for sharing (only commander can decrypt)
  sectorSecret?: string
  // Assigned organizer for this sector
  organizerPeerId?: string
  organizerPublicKey?: string
  // Participant centroid for auto-boundary suggestions
  participantCentroidLat?: number
  participantCentroidLng?: number
}

/** Cross-room organizer assignment entry */
export interface OrganizerAssignmentEntry {
  id: string
  commandRoomId: string
  sectorRoomId: string
  assigneePeerId: string
  assigneePublicKey: string
  assignedBy: string
  commanderPublicKey: string
  timestamp: number
  signature: string
}

/** Remote command entry for live room management */
export interface RemoteCommandEntry {
  id: string
  commandRoomId: string
  sectorRoomId: string
  command: RemoteCommandType
  issuedBy: string
  timestamp: number
  signature: string
  executed: boolean
  executedAt?: number
}

export type RemoteCommandType =
  | { type: 'kick_participant'; targetPeerId: string; reason?: string }
  | { type: 'pause_room' }
  | { type: 'resume_room' }
  | { type: 'update_mode'; mode: 'mic' | 'chat' | 'poi'; value: string }
  | { type: 'update_boundary'; centerLat: number; centerLng: number; radiusMeters: number }
  | { type: 'clear_all_pois' }
  | { type: 'set_alert_status'; status: 'active' | 'alert' | 'moving' }

/** Cryptographically signed role assignment stored in CRDT */
export interface RoleAssignmentEntry {
  assigneePublicKey: string
  role: Role
  timestamp: number
  roomId: string
  organizerPublicKey: string
  signature: string
}

export interface POIEntry {
  id: string
  type: QuickActionKey
  locationLat: number
  locationLng: number
  address?: string
  reporterId: string
  reporterName: string
  createdAt: number // timestamp
  expiresAt?: number
  clearedAt?: number
  clearedBy?: string
}

export interface MessageEntry {
  id: string
  type: 'text' | 'voice' | 'poi' | 'system'
  senderId: string
  senderName: string
  senderRole: Role
  timestamp: number
  content?: string
  /** Set on a copy that arrived over LoRa: the tag of the original message. */
  radioTag?: string
  audioData?: string // Base64 encoded
  duration?: number
  transcript?: string
  transcriptConfidence?: number
  poiType?: QuickActionKey
  locationLat?: number
  locationLng?: number
  address?: string
  expiresAt?: number
  // Vehicle sighting data (when poiType === 'vehicle_sighting')
  vehicleData?: {
    plate?: string
    plateConfidence?: 'full' | 'partial' | 'none'
    vehicleType?: 'sedan' | 'suv' | 'van' | 'truck' | 'unmarked' | 'other'
    vehicleColor?: string
    direction?: 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW' | 'stationary'
    description?: string
    isFollowing?: boolean
    photos?: Array<{
      id: string
      thumbnailDataUrl: string
      hasLocation: boolean
      plateOCR?: { text: string; confidence: number }
    }>
  }
  // Cryptographic authentication
  signature?: string // Base64-encoded Ed25519 signature
  senderPublicKey?: string // Base64-encoded public key for verification
  // Replay protection
  sequence?: number // Monotonically increasing per-sender sequence number
}

const roomDocuments = new Map<string, RoomDocument>()

/**
 * Create or get a room document
 */
export function getRoomDocument(roomId: string): RoomDocument {
  const existing = roomDocuments.get(roomId)
  if (existing) {
    return existing
  }

  const doc = new Y.Doc()

  // Initialize shared types
  const participants = doc.getMap<Participant>('participants')
  const speakingQueue = doc.getArray<string>('speakingQueue')
  const pois = doc.getMap<POIEntry>('pois')
  const messages = doc.getArray<MessageEntry>('messages')
  const meta = doc.getMap('meta')
  const roleAssignments = doc.getMap<RoleAssignmentEntry>('roleAssignments')

  // ICS Hierarchy shared types
  const hierarchyMeta = doc.getMap('hierarchyMeta')
  const escalations = doc.getArray<EscalatedReportEntry>('escalations')
  const broadcasts = doc.getArray<CommandBroadcastEntry>('broadcasts')
  const sectors = doc.getMap<SectorInfoEntry>('sectors')

  // Command center room management extensions
  const organizerAssignments = doc.getMap<OrganizerAssignmentEntry>('organizerAssignments')
  const remoteCommands = doc.getArray<RemoteCommandEntry>('remoteCommands')

  // Set up IndexedDB persistence
  const persistence = new IndexeddbPersistence(`crowdtalkie-room-${roomId}`, doc)

  persistence.on('synced', () => {
    console.log(`[CRDT] Room ${roomId} synced with IndexedDB`)
  })

  const roomDoc: RoomDocument = {
    doc,
    participants,
    speakingQueue,
    pois,
    messages,
    meta,
    roleAssignments,
    hierarchyMeta,
    escalations,
    broadcasts,
    sectors,
    organizerAssignments,
    remoteCommands,
    persistence,
  }

  roomDocuments.set(roomId, roomDoc)
  return roomDoc
}

/**
 * Destroy a room document (cleanup)
 */
export async function destroyRoomDocument(roomId: string): Promise<void> {
  const roomDoc = roomDocuments.get(roomId)
  if (roomDoc) {
    if (roomDoc.persistence) {
      await roomDoc.persistence.destroy()
    }
    roomDoc.doc.destroy()
    roomDocuments.delete(roomId)
    console.log(`[CRDT] Room ${roomId} document destroyed`)
  }
}

// ============= Room Meta Operations =============

export function initializeRoomMeta(
  roomDoc: RoomDocument,
  meta: RoomMeta
): void {
  roomDoc.doc.transact(() => {
    roomDoc.meta.set('name', meta.name)
    roomDoc.meta.set('centerLat', meta.center.latitude)
    roomDoc.meta.set('centerLng', meta.center.longitude)
    roomDoc.meta.set('radiusMeters', meta.radiusMeters)
    roomDoc.meta.set('createdAt', meta.createdAt.getTime())
    roomDoc.meta.set('micMode', meta.micMode)
    roomDoc.meta.set('chatMode', meta.chatMode)
    roomDoc.meta.set('poiMode', meta.poiMode)
    roomDoc.meta.set('poiExpirationMinutes', meta.poiExpirationMinutes)
    roomDoc.meta.set('isPaused', meta.isPaused)
    roomDoc.meta.set('sharingEnabled', meta.sharingEnabled)
  })
}

export function getRoomMeta(roomDoc: RoomDocument): RoomMeta | null {
  const name = roomDoc.meta.get('name') as string | undefined
  if (!name) return null

  return {
    name,
    center: {
      latitude: roomDoc.meta.get('centerLat') as number,
      longitude: roomDoc.meta.get('centerLng') as number,
    },
    radiusMeters: roomDoc.meta.get('radiusMeters') as number,
    createdAt: new Date(roomDoc.meta.get('createdAt') as number),
    micMode: (roomDoc.meta.get('micMode') as MicMode) ?? 'open',
    chatMode: (roomDoc.meta.get('chatMode') as ChatMode) ?? 'open',
    poiMode: (roomDoc.meta.get('poiMode') as POIMode) ?? 'open',
    poiExpirationMinutes: (roomDoc.meta.get('poiExpirationMinutes') as number) ?? 15,
    isPaused: (roomDoc.meta.get('isPaused') as boolean) ?? false,
    sharingEnabled: (roomDoc.meta.get('sharingEnabled') as boolean) ?? true,
  }
}

export function updateRoomMeta(
  roomDoc: RoomDocument,
  updates: Partial<RoomMeta>
): void {
  roomDoc.doc.transact(() => {
    if (updates.name !== undefined) roomDoc.meta.set('name', updates.name)
    if (updates.micMode !== undefined) roomDoc.meta.set('micMode', updates.micMode)
    if (updates.chatMode !== undefined) roomDoc.meta.set('chatMode', updates.chatMode)
    if (updates.poiMode !== undefined) roomDoc.meta.set('poiMode', updates.poiMode)
    if (updates.poiExpirationMinutes !== undefined) {
      roomDoc.meta.set('poiExpirationMinutes', updates.poiExpirationMinutes)
    }
    if (updates.isPaused !== undefined) roomDoc.meta.set('isPaused', updates.isPaused)
    if (updates.sharingEnabled !== undefined) roomDoc.meta.set('sharingEnabled', updates.sharingEnabled)
  })
}

// ============= Participant Operations =============

export function addParticipant(
  roomDoc: RoomDocument,
  participant: Participant
): void {
  roomDoc.participants.set(participant.peerId, participant)
}

export function removeParticipant(
  roomDoc: RoomDocument,
  peerId: string
): void {
  roomDoc.participants.delete(peerId)
}

export function updateParticipant(
  roomDoc: RoomDocument,
  peerId: string,
  updates: Partial<Participant>
): void {
  const current = roomDoc.participants.get(peerId)
  if (current) {
    roomDoc.participants.set(peerId, { ...current, ...updates })
  }
}

export function getParticipants(roomDoc: RoomDocument): Participant[] {
  return Array.from(roomDoc.participants.values())
}

// ============= Speaking Queue Operations =============

export function joinSpeakingQueue(
  roomDoc: RoomDocument,
  peerId: string
): void {
  const queue = roomDoc.speakingQueue.toArray()
  if (!queue.includes(peerId)) {
    roomDoc.speakingQueue.push([peerId])
  }
}

export function leaveSpeakingQueue(
  roomDoc: RoomDocument,
  peerId: string
): void {
  const queue = roomDoc.speakingQueue.toArray()
  const index = queue.indexOf(peerId)
  if (index !== -1) {
    roomDoc.speakingQueue.delete(index, 1)
  }
}

export function getSpeakingQueue(roomDoc: RoomDocument): string[] {
  return roomDoc.speakingQueue.toArray()
}

export function getCurrentSpeaker(roomDoc: RoomDocument): string | null {
  const queue = roomDoc.speakingQueue.toArray()
  return queue[0] ?? null
}

// ============= Message Operations =============

export function addMessage(
  roomDoc: RoomDocument,
  message: MessageEntry
): void {
  roomDoc.messages.push([message])
}

export function getMessages(
  roomDoc: RoomDocument,
  limit?: number
): MessageEntry[] {
  const all = roomDoc.messages.toArray()
  if (limit) {
    return all.slice(-limit)
  }
  return all
}

export function pruneOldMessages(
  roomDoc: RoomDocument,
  maxAgeMs: number
): number {
  const now = Date.now()
  const messages = roomDoc.messages.toArray()
  let pruned = 0

  // Find messages to remove (from oldest first)
  const indicesToRemove: number[] = []
  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i]
    if (msg && now - msg.timestamp > maxAgeMs) {
      indicesToRemove.push(i)
    }
  }

  // Remove in reverse order to maintain indices
  roomDoc.doc.transact(() => {
    for (let i = indicesToRemove.length - 1; i >= 0; i--) {
      const index = indicesToRemove[i]
      if (index !== undefined) {
        roomDoc.messages.delete(index, 1)
        pruned++
      }
    }
  })

  return pruned
}

// ============= POI Operations =============

export function addPOI(roomDoc: RoomDocument, poi: POIEntry): void {
  roomDoc.pois.set(poi.id, poi)
}

export function clearPOI(
  roomDoc: RoomDocument,
  poiId: string,
  clearedBy: string
): void {
  const poi = roomDoc.pois.get(poiId)
  if (poi) {
    roomDoc.pois.set(poiId, {
      ...poi,
      clearedAt: Date.now(),
      clearedBy,
    })
  }
}

export function removePOI(roomDoc: RoomDocument, poiId: string): void {
  roomDoc.pois.delete(poiId)
}

export function getActivePOIs(roomDoc: RoomDocument): POIEntry[] {
  const now = Date.now()
  return Array.from(roomDoc.pois.values()).filter((poi) => {
    // Not cleared
    if (poi.clearedAt) return false
    // Not expired
    if (poi.expiresAt && poi.expiresAt < now) return false
    return true
  })
}

export function pruneExpiredPOIs(roomDoc: RoomDocument): number {
  const now = Date.now()
  const toRemove: string[] = []

  roomDoc.pois.forEach((poi, id) => {
    if (poi.clearedAt || (poi.expiresAt && poi.expiresAt < now)) {
      toRemove.push(id)
    }
  })

  roomDoc.doc.transact(() => {
    for (const id of toRemove) {
      roomDoc.pois.delete(id)
    }
  })

  return toRemove.length
}

// ============= State Encoding =============

/**
 * Get encoded state for network sync
 */
export function encodeState(roomDoc: RoomDocument): Uint8Array {
  return Y.encodeStateAsUpdate(roomDoc.doc)
}

// Origin marker for remote updates (used to prevent echo loops)
export const REMOTE_ORIGIN = 'remote'

/**
 * Apply state update from network
 */
export function applyStateUpdate(
  roomDoc: RoomDocument,
  update: Uint8Array
): void {
  // Mark this update as coming from remote to prevent re-broadcasting
  Y.applyUpdate(roomDoc.doc, update, REMOTE_ORIGIN)
}

/**
 * Subscribe to document updates
 * @param localOnly - If true, only fires for local updates (not remote)
 */
export function onDocumentUpdate(
  roomDoc: RoomDocument,
  callback: (update: Uint8Array, origin: unknown) => void,
  localOnly: boolean = false
): () => void {
  const wrappedCallback = (update: Uint8Array, origin: unknown) => {
    // Skip remote updates if localOnly is true
    if (localOnly && origin === REMOTE_ORIGIN) {
      return
    }
    callback(update, origin)
  }
  roomDoc.doc.on('update', wrappedCallback)
  return () => roomDoc.doc.off('update', wrappedCallback)
}

// ============= Role Assignment Operations =============

/**
 * Set a role assignment (organizer only)
 */
export function setRoleAssignment(
  roomDoc: RoomDocument,
  assignment: RoleAssignmentEntry
): void {
  // Key by the assignee's public key
  roomDoc.roleAssignments.set(assignment.assigneePublicKey, assignment)
}

/**
 * Get a role assignment for a user by their public key
 */
export function getRoleAssignment(
  roomDoc: RoomDocument,
  assigneePublicKey: string
): RoleAssignmentEntry | undefined {
  return roomDoc.roleAssignments.get(assigneePublicKey)
}

/**
 * Get all role assignments
 */
export function getAllRoleAssignments(
  roomDoc: RoomDocument
): RoleAssignmentEntry[] {
  return Array.from(roomDoc.roleAssignments.values())
}

/**
 * Remove a role assignment (organizer only)
 */
export function removeRoleAssignment(
  roomDoc: RoomDocument,
  assigneePublicKey: string
): void {
  roomDoc.roleAssignments.delete(assigneePublicKey)
}

/**
 * Set the organizer's public key in room meta
 */
export function setOrganizerPublicKey(
  roomDoc: RoomDocument,
  publicKey: string
): void {
  roomDoc.meta.set('organizerPublicKey', publicKey)
}

/**
 * Get the organizer's public key from room meta
 */
export function getOrganizerPublicKey(
  roomDoc: RoomDocument
): string | undefined {
  return roomDoc.meta.get('organizerPublicKey') as string | undefined
}

// ============= ICS Hierarchy Operations =============

/**
 * Initialize hierarchy metadata for a room
 */
export function initializeHierarchyMeta(
  roomDoc: RoomDocument,
  type: HierarchyRoomType,
  parentRoomId?: string,
  commanderPublicKey?: string
): void {
  roomDoc.doc.transact(() => {
    roomDoc.hierarchyMeta.set('type', type)
    roomDoc.hierarchyMeta.set('createdAt', Date.now())
    if (parentRoomId) {
      roomDoc.hierarchyMeta.set('parentRoomId', parentRoomId)
    }
    if (commanderPublicKey) {
      roomDoc.hierarchyMeta.set('commanderPublicKey', commanderPublicKey)
    }
    roomDoc.hierarchyMeta.set('childRoomIds', [])
  })
}

/**
 * Get hierarchy metadata for a room
 */
export function getHierarchyMeta(roomDoc: RoomDocument): {
  type: HierarchyRoomType
  parentRoomId?: string
  childRoomIds: string[]
  commanderPublicKey?: string
  createdAt: Date
} | null {
  const type = roomDoc.hierarchyMeta.get('type') as HierarchyRoomType | undefined
  if (!type) return null

  return {
    type,
    parentRoomId: roomDoc.hierarchyMeta.get('parentRoomId') as string | undefined,
    childRoomIds: (roomDoc.hierarchyMeta.get('childRoomIds') as string[]) ?? [],
    commanderPublicKey: roomDoc.hierarchyMeta.get('commanderPublicKey') as string | undefined,
    createdAt: new Date(roomDoc.hierarchyMeta.get('createdAt') as number),
  }
}

/**
 * Add a sector to a command room's hierarchy
 */
export function addSectorToHierarchy(
  roomDoc: RoomDocument,
  sectorRoomId: string
): void {
  const currentChildren = (roomDoc.hierarchyMeta.get('childRoomIds') as string[]) ?? []
  if (!currentChildren.includes(sectorRoomId)) {
    roomDoc.hierarchyMeta.set('childRoomIds', [...currentChildren, sectorRoomId])
  }
}

/**
 * Remove a sector from a command room's hierarchy
 */
export function removeSectorFromHierarchy(
  roomDoc: RoomDocument,
  sectorRoomId: string
): void {
  const currentChildren = (roomDoc.hierarchyMeta.get('childRoomIds') as string[]) ?? []
  roomDoc.hierarchyMeta.set(
    'childRoomIds',
    currentChildren.filter((id) => id !== sectorRoomId)
  )
}

// ============= Escalation Operations =============

/**
 * Add an escalated report to the command room
 */
export function addEscalation(
  roomDoc: RoomDocument,
  escalation: EscalatedReportEntry
): void {
  roomDoc.escalations.push([escalation])
}

/**
 * Get all escalations
 */
export function getEscalations(
  roomDoc: RoomDocument,
  limit?: number
): EscalatedReportEntry[] {
  const all = roomDoc.escalations.toArray()
  if (limit) {
    return all.slice(-limit)
  }
  return all
}

/**
 * Get unacknowledged escalations
 */
export function getUnacknowledgedEscalations(
  roomDoc: RoomDocument
): EscalatedReportEntry[] {
  return roomDoc.escalations.toArray().filter((e) => !e.acknowledged)
}

/**
 * Acknowledge an escalation
 */
export function acknowledgeEscalation(
  roomDoc: RoomDocument,
  escalationId: string,
  acknowledgedBy: string
): void {
  const escalations = roomDoc.escalations.toArray()
  const index = escalations.findIndex((e) => e.id === escalationId)
  if (index !== -1) {
    const escalation = escalations[index]
    if (escalation) {
      roomDoc.doc.transact(() => {
        roomDoc.escalations.delete(index, 1)
        roomDoc.escalations.insert(index, [{
          ...escalation,
          acknowledged: true,
          acknowledgedBy,
          acknowledgedAt: Date.now(),
        }])
      })
    }
  }
}

/**
 * Prune old escalations
 */
export function pruneOldEscalations(
  roomDoc: RoomDocument,
  maxAgeMs: number
): number {
  const now = Date.now()
  const escalations = roomDoc.escalations.toArray()
  let pruned = 0

  const indicesToRemove: number[] = []
  for (let i = 0; i < escalations.length; i++) {
    const esc = escalations[i]
    if (esc && now - esc.timestamp > maxAgeMs) {
      indicesToRemove.push(i)
    }
  }

  roomDoc.doc.transact(() => {
    for (let i = indicesToRemove.length - 1; i >= 0; i--) {
      const index = indicesToRemove[i]
      if (index !== undefined) {
        roomDoc.escalations.delete(index, 1)
        pruned++
      }
    }
  })

  return pruned
}

// ============= Broadcast Operations =============

/**
 * Add a command broadcast
 */
export function addBroadcast(
  roomDoc: RoomDocument,
  broadcast: CommandBroadcastEntry
): void {
  roomDoc.broadcasts.push([broadcast])
}

/**
 * Get all broadcasts
 */
export function getBroadcasts(
  roomDoc: RoomDocument,
  limit?: number
): CommandBroadcastEntry[] {
  const all = roomDoc.broadcasts.toArray()
  if (limit) {
    return all.slice(-limit)
  }
  return all
}

/**
 * Get broadcasts for a specific sector
 */
export function getBroadcastsForSector(
  roomDoc: RoomDocument,
  sectorRoomId: string
): CommandBroadcastEntry[] {
  return roomDoc.broadcasts.toArray().filter((b) => {
    if (b.targetRoomIds === 'all') return true
    return b.targetRoomIds.includes(sectorRoomId)
  })
}

/**
 * Prune old broadcasts
 */
export function pruneOldBroadcasts(
  roomDoc: RoomDocument,
  maxAgeMs: number
): number {
  const now = Date.now()
  const broadcasts = roomDoc.broadcasts.toArray()
  let pruned = 0

  const indicesToRemove: number[] = []
  for (let i = 0; i < broadcasts.length; i++) {
    const bcast = broadcasts[i]
    if (bcast && now - bcast.timestamp > maxAgeMs) {
      indicesToRemove.push(i)
    }
  }

  roomDoc.doc.transact(() => {
    for (let i = indicesToRemove.length - 1; i >= 0; i--) {
      const index = indicesToRemove[i]
      if (index !== undefined) {
        roomDoc.broadcasts.delete(index, 1)
        pruned++
      }
    }
  })

  return pruned
}

// ============= Sector Info Operations =============

/**
 * Update sector info (for command room dashboard)
 */
export function updateSectorInfo(
  roomDoc: RoomDocument,
  sectorInfo: SectorInfoEntry
): void {
  roomDoc.sectors.set(sectorInfo.roomId, sectorInfo)
}

/**
 * Get all sector info
 */
export function getSectorInfos(roomDoc: RoomDocument): SectorInfoEntry[] {
  return Array.from(roomDoc.sectors.values())
}

/**
 * Get sector info by room ID
 */
export function getSectorInfo(
  roomDoc: RoomDocument,
  sectorRoomId: string
): SectorInfoEntry | undefined {
  return roomDoc.sectors.get(sectorRoomId)
}

/**
 * Remove sector info
 */
export function removeSectorInfo(
  roomDoc: RoomDocument,
  sectorRoomId: string
): void {
  roomDoc.sectors.delete(sectorRoomId)
}

/**
 * Update sector status
 */
export function updateSectorStatus(
  roomDoc: RoomDocument,
  sectorRoomId: string,
  status: 'active' | 'offline' | 'alert' | 'moving',
  participantCount?: number
): void {
  const current = roomDoc.sectors.get(sectorRoomId)
  if (current) {
    roomDoc.sectors.set(sectorRoomId, {
      ...current,
      status,
      participantCount: participantCount ?? current.participantCount,
      lastActivity: Date.now(),
    })
  }
}

/**
 * Increment pending escalations count for a sector
 */
export function incrementSectorEscalations(
  roomDoc: RoomDocument,
  sectorRoomId: string
): void {
  const current = roomDoc.sectors.get(sectorRoomId)
  if (current) {
    roomDoc.sectors.set(sectorRoomId, {
      ...current,
      pendingEscalations: current.pendingEscalations + 1,
    })
  }
}

/**
 * Reset pending escalations count for a sector
 */
export function resetSectorEscalations(
  roomDoc: RoomDocument,
  sectorRoomId: string
): void {
  const current = roomDoc.sectors.get(sectorRoomId)
  if (current) {
    roomDoc.sectors.set(sectorRoomId, {
      ...current,
      pendingEscalations: 0,
    })
  }
}

/**
 * Update sector boundary
 */
export function updateSectorBoundary(
  roomDoc: RoomDocument,
  sectorRoomId: string,
  centerLat: number,
  centerLng: number,
  radiusMeters: number
): void {
  const current = roomDoc.sectors.get(sectorRoomId)
  if (current) {
    roomDoc.sectors.set(sectorRoomId, {
      ...current,
      centerLat,
      centerLng,
      radiusMeters,
      lastActivity: Date.now(),
    })
  }
}

/**
 * Update sector participant centroid (for auto-boundary suggestions)
 */
export function updateSectorCentroid(
  roomDoc: RoomDocument,
  sectorRoomId: string,
  centroidLat: number,
  centroidLng: number
): void {
  const current = roomDoc.sectors.get(sectorRoomId)
  if (current) {
    roomDoc.sectors.set(sectorRoomId, {
      ...current,
      participantCentroidLat: centroidLat,
      participantCentroidLng: centroidLng,
      lastActivity: Date.now(),
    })
  }
}

/**
 * Assign organizer to sector
 */
export function assignOrganizerToSector(
  roomDoc: RoomDocument,
  sectorRoomId: string,
  organizerPeerId: string,
  organizerPublicKey: string
): void {
  const current = roomDoc.sectors.get(sectorRoomId)
  if (current) {
    roomDoc.sectors.set(sectorRoomId, {
      ...current,
      organizerPeerId,
      organizerPublicKey,
      leadPeerId: organizerPeerId,
      lastActivity: Date.now(),
    })
  }
}

// ============= Organizer Assignment Operations =============

/**
 * Add an organizer assignment
 */
export function addOrganizerAssignment(
  roomDoc: RoomDocument,
  assignment: OrganizerAssignmentEntry
): void {
  // Key by sector room ID since each sector can only have one organizer
  roomDoc.organizerAssignments.set(assignment.sectorRoomId, assignment)
}

/**
 * Get organizer assignment for a sector
 */
export function getOrganizerAssignment(
  roomDoc: RoomDocument,
  sectorRoomId: string
): OrganizerAssignmentEntry | undefined {
  return roomDoc.organizerAssignments.get(sectorRoomId)
}

/**
 * Get all organizer assignments
 */
export function getAllOrganizerAssignments(
  roomDoc: RoomDocument
): OrganizerAssignmentEntry[] {
  return Array.from(roomDoc.organizerAssignments.values())
}

/**
 * Remove organizer assignment
 */
export function removeOrganizerAssignment(
  roomDoc: RoomDocument,
  sectorRoomId: string
): void {
  roomDoc.organizerAssignments.delete(sectorRoomId)
}

// ============= Remote Command Operations =============

/**
 * Add a remote command
 */
export function addRemoteCommand(
  roomDoc: RoomDocument,
  command: RemoteCommandEntry
): void {
  roomDoc.remoteCommands.push([command])
}

/**
 * Get pending remote commands for a sector
 */
export function getPendingRemoteCommands(
  roomDoc: RoomDocument,
  sectorRoomId: string
): RemoteCommandEntry[] {
  return roomDoc.remoteCommands.toArray().filter(
    (cmd) => cmd.sectorRoomId === sectorRoomId && !cmd.executed
  )
}

/**
 * Get all remote commands
 */
export function getRemoteCommands(
  roomDoc: RoomDocument,
  limit?: number
): RemoteCommandEntry[] {
  const all = roomDoc.remoteCommands.toArray()
  if (limit) {
    return all.slice(-limit)
  }
  return all
}

/**
 * Mark a remote command as executed
 */
export function markCommandExecuted(
  roomDoc: RoomDocument,
  commandId: string
): void {
  const commands = roomDoc.remoteCommands.toArray()
  const index = commands.findIndex((cmd) => cmd.id === commandId)
  if (index !== -1) {
    const command = commands[index]
    if (command) {
      roomDoc.doc.transact(() => {
        roomDoc.remoteCommands.delete(index, 1)
        roomDoc.remoteCommands.insert(index, [{
          ...command,
          executed: true,
          executedAt: Date.now(),
        }])
      })
    }
  }
}

/**
 * Prune old remote commands
 */
export function pruneOldRemoteCommands(
  roomDoc: RoomDocument,
  maxAgeMs: number
): number {
  const now = Date.now()
  const commands = roomDoc.remoteCommands.toArray()
  let pruned = 0

  const indicesToRemove: number[] = []
  for (let i = 0; i < commands.length; i++) {
    const cmd = commands[i]
    // Remove executed commands older than maxAge, or unexecuted commands older than maxAge
    if (cmd && now - cmd.timestamp > maxAgeMs) {
      indicesToRemove.push(i)
    }
  }

  roomDoc.doc.transact(() => {
    for (let i = indicesToRemove.length - 1; i >= 0; i--) {
      const index = indicesToRemove[i]
      if (index !== undefined) {
        roomDoc.remoteCommands.delete(index, 1)
        pruned++
      }
    }
  })

  return pruned
}
